import {Axios} from 'services/axios';

export const durationApi = {
  createDuration: async durationData => {
    try {
      const response = await Axios.post('/admin/duration', durationData);

      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error creating duration:', error);

      let errorMessage = 'Failed to create duration';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  bulkCreateDurations: async (durations, overwrite = false) => {
    try {
      const payload = {
        durations,
        overwrite,
      };

      const response = await Axios.post('/admin/bulk-create/duration', payload);

      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error bulk creating durations:', error);

      let errorMessage = 'Failed to bulk create durations';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getAllDurations: async (options = {}) => {
    try {
      const {
        page = 1,
        limit = 20,
        unit = 'all',
        isActive = null,
        search = '',
        sortBy = 'displayOrder',
        sortOrder = 'ASC',
      } = options;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        unit,
        sortBy,
        sortOrder,
      });

      if (isActive !== null) {
        params.append('isActive', String(isActive));
      }

      if (search) {
        params.append('search', search);
      }

      const response = await Axios.fetch(`/admin/duration?${params}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const responseData = response.data.data || response.data || {};

      return responseData;
    } catch (error) {
      console.error('Error fetching durations:', error);

      let errorMessage = 'Failed to fetch durations';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getDurationById: async id => {
    try {
      if (!id) {
        throw new Error('Duration ID is required');
      }

      const response = await Axios.fetch(`/admin/duration/${id}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const duration = response.data.data || response.data;

      if (!duration) {
        throw new Error('Duration not found');
      }

      return duration;
    } catch (error) {
      console.error('Error fetching duration:', error);

      let errorMessage = 'Failed to fetch duration';

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Duration not found';
        } else {
          errorMessage =
            error.response.data?.message ||
            `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  updateDuration: async (id, durationData) => {
    try {
      if (!id) {
        throw new Error('Duration ID is required for update');
      }

      const response = await Axios.put(`/admin/duration/${id}`, durationData);

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error updating duration:', error);

      let errorMessage = 'Failed to update duration';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  deleteDuration: async id => {
    try {
      if (!id) {
        throw new Error('Duration ID is required');
      }

      const response = await Axios.delete(`/admin/duration/${id}`);

      return response.data;
    } catch (error) {
      console.error('Error deleting duration:', error);

      let errorMessage = 'Failed to delete duration';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  bulkUpdateDurations: async durations => {
    try {
      if (!Array.isArray(durations) || durations.length === 0) {
        throw new Error('Durations array is required');
      }

      const payload = {durations};

      const response = await Axios.put('/admin/duration/bulk-update', payload);

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error bulk updating durations:', error);

      let errorMessage = 'Failed to bulk update durations';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  seedStandardDurations: async () => {
    try {
      const response = await Axios.post('/admin/duration/seed-standard');

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error seeding standard durations:', error);

      let errorMessage = 'Failed to seed standard durations';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getActiveDurations: async (options = {}) => {
    try {
      // const params = new URLSearchParams();
      // console.log("params : ", params);

      const response = await Axios.fetch(
        `public/duration/active?${
          options.isActive !== undefined ? `isActive=${options.isActive}&` : ''
        }${options.sortBy ? `sortBy=${options.sortBy}&` : ''}${
          options.sortOrder ? `sortOrder=${options.sortOrder}` : ''
        }`,
      );

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const responseData = response.data.data || response.data || {};

      return responseData;
    } catch (error) {
      console.error('Error fetching active durations:', error);

      let errorMessage = 'Failed to fetch active durations';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getPublicDurationById: async id => {
    try {
      if (!id) {
        throw new Error('Duration ID is required');
      }

      const response = await Axios.fetch(`/public/duration/${id}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const duration = response.data.data || response.data;

      if (!duration) {
        throw new Error('Duration not found');
      }

      return duration;
    } catch (error) {
      console.error('Error fetching public duration:', error);

      let errorMessage = 'Failed to fetch duration';

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Duration not found or not active';
        } else {
          errorMessage =
            error.response.data?.message ||
            `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getDefaultDuration: async () => {
    try {
      const response = await Axios.fetch('/public/duration/default');

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const durationData = response.data.data || response.data;

      return durationData;
    } catch (error) {
      console.error('Error fetching default duration:', error);

      let errorMessage = 'Failed to fetch default duration';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getAvailableUnits: async () => {
    try {
      const response = await Axios.fetch('/public/duration/available-units');

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const unitsData = response.data.data || response.data;

      return unitsData;
    } catch (error) {
      console.error('Error fetching available units:', error);

      let errorMessage = 'Failed to fetch available units';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  convertDuration: async (value, unit, targetUnit) => {
    try {
      if (!value || !unit || !targetUnit) {
        throw new Error('Value, unit, and targetUnit are required');
      }

      const payload = {value, unit, targetUnit};

      const response = await Axios.post('/public/duration/convert', payload);

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error converting duration:', error);

      let errorMessage = 'Failed to convert duration';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  calculateEquivalentDays: duration => {
    const {value, unit} = duration;

    switch (unit) {
      case 'day':
        return value;
      case 'week':
        return value * 7;
      case 'month':
        return value * 30;
      case 'quarter':
        return value * 90;
      case 'biannual':
        return value * 180;
      case 'year':
        return value * 365;
      default:
        return value;
    }
  },

  formatDurationLabel: duration => {
    if (!duration) return '';

    const {name, value, unit, displayLabel} = duration;

    if (displayLabel) return displayLabel;
    if (name) return name;

    const unitLabels = {
      day: 'Day',
      week: 'Week',
      month: 'Month',
      quarter: 'Quarter',
      biannual: '6 Months',
      year: 'Year',
      custom: 'Custom',
    };

    const unitLabel = unitLabels[unit] || unit;
    const plural = value > 1 ? 's' : '';

    return `${value} ${unitLabel}${plural}`;
  },

  getUnitLabel: unit => {
    const unitLabels = {
      day: 'Day',
      week: 'Week',
      month: 'Month',
      quarter: 'Quarter',
      biannual: '6 Months',
      year: 'Year',
      custom: 'Custom',
    };

    return unitLabels[unit] || unit;
  },

  getAllUnitsWithLabels: () => {
    return [
      {value: 'day', label: 'Day'},
      {value: 'week', label: 'Week'},
      {value: 'month', label: 'Month'},
      {value: 'quarter', label: 'Quarter'},
      {value: 'biannual', label: '6 Months'},
      {value: 'year', label: 'Year'},
      {value: 'custom', label: 'Custom'},
    ];
  },

  validateDurationData: durationData => {
    const errors = {};

    if (!durationData.name || durationData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    if (!durationData.value || durationData.value < 1) {
      errors.value = 'Value must be at least 1';
    }

    if (!durationData.unit) {
      errors.unit = 'Unit is required';
    }

    if (durationData.displayOrder < 0) {
      errors.displayOrder = 'Display order cannot be negative';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },
};
